import { useEffect, useState } from 'react'
import { PencilIcon, XIcon } from 'lucide-react'
import { Box, Drawer, IconButton, Link, Stack, Typography, useTheme } from '@mui/material'

import { Button, Input } from '@repo/ui'
import { SelectField } from 'components'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import { useUpdateCompetitorProfile } from 'hooks/api'
import type { SeoCompetitor } from 'services'

import { dateTime } from '../shared'
import { competitorEvidenceLines, KIND_LABEL, ReviewChip } from './seoReview'

const DRAWER_WIDTH = 560

/** Profile fields in the order the competition.md matrix shows them. */
const FIELDS: Array<{ key: keyof SeoCompetitor['profile'] | 'name' | 'notes'; label: string; long?: boolean }> = [
  { key: 'name', label: 'Name' },
  { key: 'displayUrl', label: 'URL as shown' },
  { key: 'segment', label: 'Segment' },
  { key: 'hq', label: 'HQ' },
  { key: 'founded', label: 'Founded' },
  { key: 'funding', label: 'Funding / stage', long: true },
  { key: 'scale', label: 'Scale / revenue', long: true },
  { key: 'pricing', label: 'Pricing', long: true },
  { key: 'notableClients', label: 'Notable clients', long: true },
  { key: 'approxClients', label: '~Clients' },
  { key: 'differentiation', label: 'Differentiation vs Vera', long: true },
  { key: 'notes', label: 'Notes', long: true },
]

type Draft = Record<string, string>

const toDraft = (c: SeoCompetitor): Draft => ({
  name: c.name ?? '',
  notes: c.notes ?? '',
  ...Object.fromEntries(
    FIELDS.filter((f) => f.key !== 'name' && f.key !== 'notes').map((f) => [
      f.key,
      String((c.profile as unknown as Record<string, unknown>)[f.key] ?? ''),
    ]),
  ),
  riskRank: c.profile.riskRank === null ? '' : String(c.profile.riskRank),
  sources: c.profile.sources.join('\n'),
})

/**
 * VRG-643 follow-up — a competitor's profile. The platform is the source of truth:
 * vera-inc/strategy/competition.md is generated from these fields (`seo -- competitors
 * export-md`). View for staff, edit for platform admins.
 */
export const CompetitorProfileDrawer = ({
  competitor,
  onClose,
}: {
  competitor: SeoCompetitor | null
  onClose: () => void
}) => {
  const theme = useTheme()
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const save = useUpdateCompetitorProfile()
  const { open: notify } = useSnackbar()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Draft>({})

  useEffect(() => {
    if (competitor) setDraft(toDraft(competitor))
    setEditing(false)
  }, [competitor])

  if (!competitor) return null
  const p = competitor.profile
  const value = (k: string) =>
    k === 'name'
      ? competitor.name
      : k === 'notes'
      ? competitor.notes
      : ((p as unknown as Record<string, unknown>)[k] as string | null)

  const submit = () => {
    const patch: Record<string, string | number | string[] | null> = {}
    const before = toDraft(competitor)
    for (const f of FIELDS) {
      if (draft[f.key] !== before[f.key]) patch[f.key] = draft[f.key].trim() || null
    }
    if (draft.riskRank !== before.riskRank)
      patch.riskRank = draft.riskRank ? Number(draft.riskRank) : null
    if (draft.sources !== before.sources)
      patch.sources = draft.sources
        .split(/\s+/)
        .map((s) => s.trim())
        .filter(Boolean)
    if (!Object.keys(patch).length) {
      setEditing(false)
      return
    }
    save.mutate(
      { domain: competitor.domain, patch },
      {
        onSuccess: () => {
          notify({ type: 'success', text: `Saved ${competitor.domain}. Run export-md to update competition.md.` })
          setEditing(false)
        },
        onError: (e) => notify({ type: 'error', text: (e as Error).message }),
      },
    )
  }

  const evidence = competitorEvidenceLines(competitor.evidence)

  return (
    <Drawer
      anchor="right"
      open={!!competitor}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          margin: 4,
          borderRadius: 3,
          maxHeight: 'calc(100vh - 32px)',
          minWidth: DRAWER_WIDTH,
          maxWidth: DRAWER_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          backgroundImage: 'none',
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Stack sx={{ p: 6, pb: 4, flexShrink: 0 }} spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={3}>
          <Typography variant="body1" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
            {competitor.name ?? competitor.domain}
            <ReviewChip status={competitor.status} />
          </Typography>
          <Stack direction="row" alignItems="center" spacing={3} sx={{ flexShrink: 0 }}>
            {editing ? (
              <>
                <Button variant="secondary" onClick={() => setEditing(false)} disabled={save.isPending}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={submit} loading={save.isPending}>
                  Save
                </Button>
              </>
            ) : (
              <Button
                variant="secondary"
                startIcon={<PencilIcon size={18} strokeWidth={1} />}
                disabled={!isAdmin}
                disabledReason={!isAdmin ? 'Only platform admins can edit competitor profiles.' : undefined}
                onClick={() => setEditing(true)}
              >
                Edit
              </Button>
            )}
            <IconButton onClick={onClose} aria-label="Close profile" sx={{ color: theme.palette.text.primary }}>
              <XIcon strokeWidth={1} />
            </IconButton>
          </Stack>
        </Stack>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          <Link href={`https://${competitor.domain}`} target="_blank" rel="noreferrer noopener" color="inherit">
            {competitor.domain}
          </Link>{' '}
          · {KIND_LABEL[competitor.kind]}
          {competitor.kind === 'business' ? ' (Direct)' : ''}. The source for
          strategy/competition.md: edit here, then run{' '}
          <code>seo -- competitors export-md</code> to update vera-inc.
        </Typography>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', px: 6, pb: 6 }}>
        {editing ? (
          <Stack spacing={4}>
            <SelectField
              label="Risk (1 = most threatening)"
              value={draft.riskRank ?? ''}
              onChange={(v) => setDraft({ ...draft, riskRank: v as string })}
              options={[
                { value: '', label: 'TBV' },
                ...Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) })),
              ]}
            />
            {FIELDS.map((f) => (
              <Input
                key={f.key}
                label={f.label}
                value={draft[f.key] ?? ''}
                onChange={(v: string) => setDraft({ ...draft, [f.key]: v })}
                multiline={f.long}
                minRows={f.long ? 2 : undefined}
                placeholder="TBV"
              />
            ))}
            <Input
              label="Sources (one URL per line)"
              value={draft.sources ?? ''}
              onChange={(v: string) => setDraft({ ...draft, sources: v })}
              multiline
              minRows={3}
            />
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Row label="Risk" value={p.riskRank === null ? null : `${p.riskRank} of 10`} />
            {FIELDS.map((f) => (
              <Row key={f.key} label={f.label} value={value(f.key)} />
            ))}
            <Box>
              <Typography variant="body3" color="text.secondary">
                Sources
              </Typography>
              {p.sources.length ? (
                p.sources.map((s) => (
                  <Typography key={s} variant="body3" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                    <Link href={s} target="_blank" rel="noreferrer noopener">
                      {s}
                    </Link>
                  </Typography>
                ))
              ) : (
                <Typography variant="body3">TBV</Typography>
              )}
            </Box>
            {evidence.length ? <Row label="Why it was proposed" value={evidence.join(' ')} /> : null}
            <Typography variant="body3" color="text.secondary">
              Last updated {p.updatedAt ? dateTime(p.updatedAt) : '—'}
              {p.updatedBy ? ` by ${p.updatedBy}` : ''}.
            </Typography>
          </Stack>
        )}
      </Box>
    </Drawer>
  )
}

const Row = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <Box>
    <Typography variant="body3" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.5 }}>
      {value?.trim() ? value.replace(/\*\*/g, '') : 'TBV'}
    </Typography>
  </Box>
)
